import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronDown, ChevronRight, ArrowRight, FolderTree } from "lucide-react";

export default function Subjects() {
  const { subjectId } = useParams();
  const [subjects, setSubjects] = useState([]);
  const [parents, setParents] = useState([]);
  const [topics, setTopics] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/subjects").then((r) =>
      // Hide the "math" subject tile — Math Lab has its own dedicated sidebar
      // entry and lives at /app/math, so it shouldn't appear as a subject card.
      setSubjects((r.data || []).filter((s) => s.id !== "math"))
    );
  }, []);

  useEffect(() => {
    if (subjectId) {
      Promise.all([
        api.get("/parents", { params: { subject_id: subjectId } }),
        api.get("/topics", { params: { subject_id: subjectId, parent_id: "none" } }),
      ]).then(([pRes, oRes]) => {
        setParents(pRes.data);
        setOrphans(oRes.data);
        setExpanded({});
        setTopics([]);
      });
    }
  }, [subjectId]);

  const togglePar = async (pid) => {
    if (expanded[pid]) {
      setExpanded({ ...expanded, [pid]: null });
      return;
    }
    const r = await api.get("/topics", { params: { parent_id: pid } });
    setExpanded({ ...expanded, [pid]: r.data });
  };

  const activeSubject = subjects.find((s) => s.id === subjectId);

  if (!subjectId) {
    return (
      <div data-testid="subjects-page">
        <h1 className="font-display text-4xl font-bold mb-3">Subjects</h1>
        <p className="text-lg mb-8" style={{ color: "var(--text-secondary)" }}>
          Every MCAT content category, organized by topic and subtopic.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {subjects.map((s) => (
            <Link
              key={s.id}
              to={`/app/subjects/${s.id}`}
              className="pm-card pm-card-hover p-6 relative overflow-hidden"
              data-testid={`subject-tile-${s.id}`}
            >
              <div className="absolute top-3 right-3 text-4xl opacity-40">{s.emoji}</div>
              <div className="pm-badge mb-3" style={{ background: "var(--muted)", color: s.color }}>
                {s.topic_count} topics
              </div>
              <h3 className="font-display text-2xl font-bold mb-2">{s.name}</h3>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{s.description}</p>
              <div className="mt-4 flex items-center gap-1.5 text-sm font-medium" style={{ color: s.color }}>
                Explore <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="subject-detail-page">
      <Link to="/app/subjects" className="text-sm underline mb-6 inline-block" style={{ color: "var(--primary)" }} data-testid="back-subjects-btn">
        ← All subjects
      </Link>
      <div className="mb-8">
        <div className="text-5xl mb-3">{activeSubject?.emoji}</div>
        <h1 className="font-display text-4xl font-bold">{activeSubject?.name}</h1>
        <p className="mt-2 text-lg" style={{ color: "var(--text-secondary)" }}>{activeSubject?.description}</p>
      </div>

      {/* Parent hierarchy */}
      <div className="space-y-3">
        {parents.filter(p => p.topic_count > 0).map((p) => (
          <div key={p.id} className="pm-card overflow-hidden" data-testid={`parent-${p.id}`}>
            <button
              onClick={() => togglePar(p.id)}
              className="w-full flex items-center justify-between p-5 hover:bg-[var(--muted)] transition-colors"
              data-testid={`parent-toggle-${p.id}`}
            >
              <div className="flex items-center gap-3">
                {expanded[p.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <FolderTree size={16} style={{ color: activeSubject?.color }} />
                <span className="font-display font-bold text-lg">{p.name}</span>
              </div>
              <span className="pm-badge" style={{ background: "var(--muted)", color: "var(--text)" }}>
                {p.topic_count} topics
              </span>
            </button>
            {expanded[p.id] && (
              <div className="border-t px-3 py-3 space-y-3" style={{ borderColor: "var(--border)" }} data-testid={`parent-children-${p.id}`}>
                {p.study_checklist?.length > 0 && (
                  <div
                    className="rounded-xl p-3"
                    style={{ background: "var(--muted)", border: "1px dashed var(--border)" }}
                    data-testid={`parent-checklist-${p.id}`}
                  >
                    <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>
                      What you need to know
                    </div>
                    <ul className="space-y-1">
                      {p.study_checklist.map((item, i) => (
                        <li
                          key={i}
                          className="text-sm flex gap-2"
                          data-testid={`parent-checklist-${p.id}-item-${i}`}
                        >
                          <span style={{ color: activeSubject?.color }}>✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="space-y-2">
                {expanded[p.id].map((t) => (
                  <div key={t.id} className="rounded-xl border" style={{ borderColor: "var(--border)" }}>
                    <Link
                      to={`/app/topic/${t.id}`}
                      className="p-3 flex items-center justify-between hover:bg-[var(--muted)] rounded-xl transition-colors"
                      data-testid={`child-topic-${t.id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-sm">{t.name}</div>
                        {t.analogy && <span className="text-xs" style={{ color: "var(--text-muted)" }}>✦ curated</span>}
                        {t.subtopics?.length > 0 && (
                          <span className="pm-badge" style={{ background: "var(--muted)", color: "var(--text-muted)", fontSize: "0.65rem" }}>
                            {t.subtopics.length} subtopics
                          </span>
                        )}
                      </div>
                      <ArrowRight size={14} style={{ color: "var(--text-muted)" }} />
                    </Link>
                    {t.subtopics?.length > 0 && (
                      <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1.5" data-testid={`topic-subtopics-${t.id}`}>
                        {t.subtopics.map((sub, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-1 rounded-full"
                            style={{ background: "var(--muted)", color: "var(--text-secondary)" }}
                            data-testid={`subtopic-chip-${t.id}-${i}`}
                          >
                            {sub}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {orphans.length > 0 && (
          <div className="pm-card p-5" data-testid="orphans-section">
            <div className="font-display font-bold text-lg mb-3">Other topics</div>
            <div className="grid md:grid-cols-2 gap-2">
              {orphans.slice(0, 60).map((t) => (
                <Link
                  key={t.id}
                  to={`/app/topic/${t.id}`}
                  className="p-3 rounded-xl hover:bg-[var(--muted)] text-sm"
                  data-testid={`orphan-topic-${t.id}`}
                >
                  {t.name}
                </Link>
              ))}
            </div>
            {orphans.length > 60 && (
              <div className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
                Showing 60 of {orphans.length} uncategorized topics.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
